import { baseApi } from './base';
import type { Summary } from '@/types/summary';
import type {
  Office,
  UpdateOfficeRequest,
  OfficeUser,
} from '@/types/office';
import type { Profile, PermissionModule } from '@/types/profile';

export const officeService = {
  async getSummary(): Promise<Summary> {
    return baseApi.request<Summary>('/office/summary');
  },

  async getOfficeUsers(params: { limit: number; offset: number; ordering: string; search: string; is_active?: string; profiles?: string; }): Promise<{ results: OfficeUser[], count: number }> {
    const queryParams: Record<string, string> = {
      limit: params.limit.toString(),
      offset: params.offset.toString(),
      ordering: params.ordering,
      search: params.search,
    };

    if (params.is_active !== undefined) {
      queryParams.is_active = params.is_active;
    }

    if (params.profiles !== undefined) {
      queryParams.profiles = params.profiles;
    }

    const query = new URLSearchParams(queryParams).toString();
    return baseApi.request<{ results: OfficeUser[], count: number }>(`/offices/users/?${query}`);
  },

  async getOffice(id: string): Promise<Office> {
    return baseApi.request<Office>(`/offices/${id}/`);
  },

  async updateOffice(id: string, data: UpdateOfficeRequest): Promise<Office> {
    return baseApi.request<Office>(`/offices/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async getProfiles(): Promise<Profile[]> {
    return baseApi.request<Profile[]>('/users/profiles/');
  },

  async createProfile(data: Partial<Profile>): Promise<Profile> {
    return baseApi.request<Profile>('/users/profiles/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateProfile(id: number, data: Partial<Profile>): Promise<Profile> {
    return baseApi.request<Profile>(`/users/profiles/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  async deleteProfile(id: number): Promise<void> {
    return baseApi.request<void>(`/users/profiles/${id}/`, {
      method: 'DELETE',
    });
  },

  async getPermissions(): Promise<PermissionModule[]> {
    return baseApi.request<PermissionModule[]>('/users/permissions/');
  },

  async inviteUser(data: { name: string; email: string; role: string; profiles: number[] }): Promise<void> {
    return baseApi.request<void>('/offices/users/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async resendInvite(id: string): Promise<void> {
    return baseApi.request<void>(`/offices/users/${id}/resend_invite/`, {
      method: 'POST',
    });
  },

  async updateUserStatus(id: string, data: { is_active: boolean }): Promise<void> {
    return baseApi.request<void>(`/offices/users/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateUser(id: string, data: any): Promise<void> {
    return baseApi.request<void>(`/offices/users/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
};
